import { Card } from 'antd';
import { FC, ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useIsMutating } from 'react-query';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { PageNodesQueryKeys, PagesViewMode, useDeletePageNodeMutation, useGetByIdPageNodeQuery, useGetRootPageNodeQuery } from '@/modules/Pages';

import { PageNodesOverviewView } from './PageNodesOverviewView';

interface Props {
  siteId: string;
}

export const PageNodesOverviewContainer: FC<Props> = memo(({ siteId }) => {
  const { nodeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appMessageApi = AppMessage.useControls();

  const {
    data: rootNodeData,
    error: rootNodeError,
    isLoading: isRootNodeLoading,
    isError: isRootNodeError
  } = useGetRootPageNodeQuery(
    siteId
  )({
    enabled: !!siteId && !nodeId
  });

  const {
    data: pageNodeData,
    error: pageNodeError,
    isLoading: isPageNodeLoading,
    isError: isPageNodeError
  } = useGetByIdPageNodeQuery(
    siteId,
    nodeId || ''
  )({
    enabled: !isRootNodeLoading && !!siteId && !!nodeId
  });

  const isEditingCurrentPageNode = useMemo(
    () =>
      pageNodeData
        ? location.pathname === AppRoutes.PageNodesByIdEdit
          .replace(':siteId', siteId).replace(':nodeId', pageNodeData.id)
        : false,
    [pageNodeData, location.pathname, siteId]
  );

  const isEditNodeMutating = !!useIsMutating([PageNodesQueryKeys.Edit, siteId, nodeId], { exact: true });
  const isCreateNodeMutating = !!useIsMutating([PageNodesQueryKeys.Create, siteId], { exact: true });

  useEffect(() => {
    if (rootNodeData?.id && !nodeId && location.pathname.includes(`/${ PagesViewMode.Structure }`)) {
      navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', rootNodeData.id), { replace: true });
    }
  }, [rootNodeData?.id, nodeId, location.pathname]);

  const { mutate: deleteNodeMutation, isLoading: isDeleteNodeMutationLoading } = useDeletePageNodeMutation(nodeId ?? '', siteId)({
    onSuccess: () => {
      appMessageApi.success('Ноду видалено успішно!');

      navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', pageNodeData?.parentNode?.id ?? ''));
    },
    onError: (err) => {
      messageOnError(err, appMessageApi);
    }
  });

  const handleDeleteNode = useCallback(() => {
    deleteNodeMutation();
  }, []);

  return (
    <Card>
      <FallbackedLayout
        data={ pageNodeData }
        isLoading={ isRootNodeLoading || isPageNodeLoading }
        error={ pageNodeError || rootNodeError }
        isError={ isPageNodeError || isRootNodeError }
      >
        { (pageNode): ReactElement => (pageNode &&
          <PageNodesOverviewView
            siteId={ siteId }
            disableControlButtons={
              isEditNodeMutating ||
              isDeleteNodeMutationLoading ||
              isCreateNodeMutating ||
              isPageNodeLoading
            }
            pageNode={ pageNode }
            isPageNodeLoading={ isPageNodeLoading }
            isEditingCurrentPageNode={ isEditingCurrentPageNode }
            isDeleteButtonLoading={ isDeleteNodeMutationLoading }
            handleDeleteNode={ handleDeleteNode }
          />
        ) }
      </FallbackedLayout>
    </Card>
  );
});
